import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import Card from "shared/Card";
import styles from "./PublishingV2InfoForm.module.css";
import Checkbox from "shared/@forms/Checkbox";
import InputItem from "Events/Components/InputItem";
import AddButton from "Events/Components/AddButton";
import { Select } from "shared/Select/Select";
import AddGroupModal from "Events/Components/AddGroupModal";
import { GroupsGet } from "Events/Components/TicketGroup/GroupsGet";
import VisibleToModal from "Events/Components/VisibleToModal";
import DateV2Section from "../DateV2Section";
import moment from "moment-timezone";
import createNotification from "shared/@utils/createNotification";

const timezoneLabelToIana = {
  "Pacific Time (PT)": "America/Los_Angeles",
  "Mountain Time (MT)": "America/Boise",
  "Central Time (CT)": "America/Chicago",
  "Eastern Time (ET)": "America/New_York",
  "Alaska Time (AKT)": "America/Anchorage",
  "Hawaii-Aleutian Time (HAT)": "America/Adak",
  "Hawaii-Honolulu (HST)": "Pacific/Honolulu",
  "Australian Eastern Standard Time (AEST)": "Australia/Sydney",
  "Australian Central Standard Time (ACST)": "Australia/Adelaide",
  "Australian Western Standard Time (AWST)": "Australia/Perth",
  "New Zealand Standard Time (NZST)": "Pacific/Auckland",
  "Australian Eastern Daylight Time (AEDT)": "Australia/Sydney",
  "New Zealand Daylight Time (NZDT)": "Pacific/Auckland",
};

const toIanaTimeZone = (timeZone) =>
  timezoneLabelToIana[timeZone] || timeZone || "America/Los_Angeles";

const PACIFIC_TIMEZONE_LABEL = "Pacific Time (PT)";
const PACIFIC_IANA_TIMEZONE = "America/Los_Angeles";

const buildPacificRulePurchaseStartDate = (
  eventStartDate,
  eventStartTimeZone,
  daysBefore
) => {
  if (!eventStartDate || !eventStartTimeZone) return null;

  const eventIana = toIanaTimeZone(eventStartTimeZone);

  const eventLocalDate = moment
    .tz(eventStartDate, eventIana)
    .format("YYYY-MM-DD");

  return moment
    .tz(eventLocalDate, "YYYY-MM-DD", PACIFIC_IANA_TIMEZONE)
    .subtract(daysBefore, "days")
    .set({
      hour: 10,
      minute: 0,
      second: 0,
      millisecond: 0,
    })
    .utc()
    .toISOString();
};

export function PublishingV2InfoForm({
  publishingInfo,
  onPublishChange,
  pageMode,
  onDateTimeChange,
  onGroupChange,
  homesitesGroups,
  onCaptionChange,
  onDisplayImageChange,
  isLocal2,
  onSetHomesiteTicketChange,
}) {
  const {
    data: groupData,
    isLoading: isGroupsLoading,
    refetch: refetchGroupsGet,
  } = GroupsGet();
  const [groups, setGroups] = useState([]);
  const [doUpdate, setDoUpdate] = useState(true);
  const [doUpdate2, setDoUpdate2] = useState(true);
  const [updatedState, setUpdatedState] = useState(null);
  const [updatedStateValue, setUpdatedStateValue] = useState(null);
  const [checkboxStates, setCheckboxStates] = useState({
    marketing: {
      tentative: {
        value: publishingInfo?.marketing?.tentative?.saved ?? false,
        permission: publishingInfo?.marketing?.tentative?.permission ?? "write",
      },
      active: {
        value: publishingInfo?.marketing?.active?.saved ?? false,
        permission: publishingInfo?.marketing?.active?.permission ?? "write",
      },
    },
  });
  const [inputValues, setInputValues] = useState({});
  const initialInputValuesRef = useRef({});
  const [loadingValue, setLoadingValue] = useState({});
  const [openVisibleToModal, setOpenVisibleToModal] = useState(false);
  const [visibleTo, setVisibleTo] = useState(false);
  const [bookingStatusSelected, setBookingStatusSelected] = useState("");
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(true);
  const [ticketsSaleOpen, setTicketsSaleOpen] = useState(false);
  const [primaryCaptionText, setprimaryCaptionText] = useState(
    publishingInfo?.marketing?.primaryCaption?.saved ||
      publishingInfo?.marketing?.primaryCaption?.value ||
      ""
  );
  const [secondaryCaptionText, setSecondaryCaptionText] = useState(
    publishingInfo?.marketing?.secondaryCaption?.saved ||
      publishingInfo?.marketing?.secondaryCaption?.value ||
      ""
  );

  const [displayImage, setDisplayImage] = useState(
    publishingInfo?.marketing?.displayImage?.saved ||
      publishingInfo?.marketing?.displayImage?.value ||
      ""
  );

  const isRulesetLocal = publishingInfo.event.ruleset.value;

  useEffect(() => {
    if (groupData) {
      setGroups(groupData);
    }
  }, [groupData]);

  useEffect(() => {
    const newPrimaryCaption =
      publishingInfo?.marketing?.primaryCaption?.saved ||
      publishingInfo?.marketing?.primaryCaption?.value ||
      "";

    const newSecondaryCaption =
      publishingInfo?.marketing?.secondaryCaption?.saved ||
      publishingInfo?.marketing?.secondaryCaption?.value ||
      "";

    const newDisplayImage =
      publishingInfo?.marketing?.displayImage?.saved ||
      publishingInfo?.marketing?.displayImage?.value ||
      "";

    const newEventDate =
      publishingInfo?.event?.dates?.eventStartDate?.saved ??
      publishingInfo?.event?.dates?.eventStartDate?.value ??
      "";

    const newPurchaseBegins =
      publishingInfo?.event?.dates?.purchaseStartDate?.saved ??
      publishingInfo?.event?.dates?.purchaseStartDate?.value ??
      "";

    const newPurchaseEnds =
      publishingInfo?.event?.dates?.purchaseEndDate?.saved ??
      publishingInfo?.event?.dates?.purchaseEndDate?.value ??
      "";

    const newPurchaseStartTimeZone =
      publishingInfo?.event?.dates?.purchaseStartTimeZone?.saved ??
      publishingInfo?.event?.dates?.purchaseStartTimeZone?.value ??
      "";

    const newEventStartTimeZone =
      publishingInfo?.event?.dates?.eventStartTimeZone?.saved ??
      publishingInfo?.event?.dates?.eventStartTimeZone?.value ??
      "";

    const newVisibleCalendar =
      publishingInfo?.marketing?.visibleCalendar?.saved ??
      publishingInfo?.marketing?.visibleCalendar?.value ??
      false;

    const isPurchaseStartCorrect = (() => {
      if (!newPurchaseBegins || !newEventDate) return false;

      const expectedPurchase = buildPacificRulePurchaseStartDate(
        newEventDate,
        newEventStartTimeZone || PACIFIC_TIMEZONE_LABEL,
        14
      );

      if (!expectedPurchase) {
        return false;
      }

      return moment(newPurchaseBegins).isSame(
        moment(expectedPurchase),
        "minute"
      );
    })();

    const isPurchaseEndCorrect = (() => {
      if (!newPurchaseEnds) return false;

      const newEventEndDate =
        publishingInfo?.event?.dates?.eventEndDate?.saved ??
        publishingInfo?.event?.dates?.eventEndDate?.value ??
        "";

      if (!newEventEndDate) return false;

      return moment(newPurchaseEnds).isSame(moment(newEventEndDate));
    })();

    let homesiteTicketChecks = [];

    const areHomesiteTicketsCorrect = (() => {
      if (!newEventDate || !publishingInfo?.tickets) return false;

      const expectedPurchaseLocal = buildPacificRulePurchaseStartDate(
        newEventDate,
        newEventStartTimeZone || PACIFIC_TIMEZONE_LABEL,
        30
      );

      if (!expectedPurchaseLocal) {
        return false;
      }

      return publishingInfo.tickets.every((ticketSet) =>
        ticketSet.tickets.every((ticket) => {
          const name =
            ticket?.name?.saved?.toLowerCase() ??
            ticket?.name?.value?.toLowerCase() ??
            "";

          if (!name.includes("homesite")) return true;

          const purchaseDateStr =
            ticket.purchaseStartDate?.saved ??
            ticket.purchaseStartDate?.value ??
            "";

          const ticketPurchaseTz =
            ticket.purchaseStartTimeZone?.saved ??
            ticket.purchaseStartTimeZone?.value ??
            "";

          const timezoneMatchesPurchaseTimeZone =
            ticketPurchaseTz === newPurchaseStartTimeZone;

          const purchaseIana = toIanaTimeZone(ticketPurchaseTz);
          const purchaseLocal = moment.tz(purchaseDateStr, purchaseIana);

          const dateMatches = moment(purchaseDateStr).isSame(
            moment(expectedPurchaseLocal),
            "minute"
          );
          const matches = timezoneMatchesPurchaseTimeZone && dateMatches;

          homesiteTicketChecks.push({
            ticketSet:
              ticketSet.ticketSet?.saved ?? ticketSet.ticketSet?.value ?? "",
            ticketName: ticket?.name?.saved ?? ticket?.name?.value ?? "",
            purchaseDateStr,
            ticketPurchaseTz,
            expectedPurchaseTz: newPurchaseStartTimeZone,
            purchaseLocal: purchaseLocal.format(),
            expectedPurchaseLocal: moment(expectedPurchaseLocal)
              .tz(purchaseIana)
              .format(),
            timezoneMatchesPurchaseTimeZone,
            dateMatches,
            matches,
          });

          return matches;
        })
      );
    })();

    // console.log("HomeSite checkbox checks", {
    //   isPurchaseStartCorrect,
    //   isPurchaseEndCorrect,
    //   areHomesiteTicketsCorrect,
    //   newEventDate,
    //   newPurchaseBegins,
    //   newPurchaseEnds,
    //   newEventStartTimeZone,
    //   newPurchaseStartTimeZone,
    //   homesiteTicketChecks,
    // });

    if (doUpdate2) {
      setPublishOpen(
        isPurchaseStartCorrect &&
          isPurchaseEndCorrect &&
          areHomesiteTicketsCorrect
      );
    } else {
      setDoUpdate2(true);
    }

    setTicketsSaleOpen(newVisibleCalendar);

    setprimaryCaptionText(decodeURI(newPrimaryCaption));
    setSecondaryCaptionText(decodeURI(newSecondaryCaption));
    setDisplayImage(newDisplayImage);

    setVisibleTo(
      publishingInfo.marketing?.visibleTo?.saved ||
        publishingInfo.marketing?.visibleTo?.value
    );
  }, [publishingInfo]);

  const getCheckboxValue = (field) => {
    if (field && typeof field === "object") {
      if (
        field.saved !== undefined &&
        field.saved !== null &&
        field.saved !== ""
      ) {
        return field.saved;
      }
      if (
        field.value !== undefined &&
        field.value !== null &&
        field.value !== ""
      ) {
        return field.value;
      }
    } else if (field !== undefined && field !== null && field !== "") {
      return field;
    }
    return false; // default
  };

  useEffect(() => {
    if (doUpdate) {
      setCheckboxStates({
        marketing: {
          tentative: {
            value: getCheckboxValue(publishingInfo?.marketing?.tentative),
            permission: "write",
          },
          active: {
            value: getCheckboxValue(publishingInfo?.marketing?.active),
            permission: "write",
          },
        },
      });
    }

    if (
      updatedStateValue &&
      updatedState &&
      (publishingInfo?.marketing[updatedState]?.saved ??
        publishingInfo?.marketing[updatedState]?.value ??
        false) === updatedStateValue
    ) {
      setDoUpdate(true);
      setUpdatedState(null);
      setUpdatedStateValue(null);
    }
  }, [publishingInfo, doUpdate]);
  useEffect(() => {
    setInputValues({
      purchase:
        publishingInfo?.analytics?.sales.capacity?.purchase?.saved ??
        publishingInfo?.analytics?.sales.capacity?.purchase?.value ??
        "",

      platinum:
        publishingInfo?.analytics?.sales.capacity?.platinum?.saved ??
        publishingInfo?.analytics?.sales.capacity?.platinum?.value ??
        "",

      estimatedAttendance:
        publishingInfo?.analytics?.sales.capacity?.estimatedAttendance?.saved ??
        publishingInfo?.analytics?.sales.capacity?.estimatedAttendance?.value ??
        "",
    });
  }, [publishingInfo]);

  const [isRequired, setIsRequired] = useState({
    visibleTo: false,
    displayImage: false,
  });

  useEffect(() => {
    const updatedIsRequired = { ...isRequired };

    if (publishingInfo?.marketing?.visibleTo?.required !== undefined) {
      updatedIsRequired.visibleTo =
        publishingInfo?.marketing?.visibleTo?.required;
    }
    if (publishingInfo?.marketing?.displayImage?.required !== undefined) {
      updatedIsRequired.displayImage =
        publishingInfo?.marketing?.displayImage?.required;
    }

    setIsRequired(updatedIsRequired);
  }, [publishingInfo]);

  const handleCheckboxChange = (section, field, isChecked) => {
    const newStates = { ...checkboxStates };

    const fullPath =
      section === "analytics" && field === "soldOut"
        ? "analytics.sales.soldOut"
        : `${section}.${field}`;

    const newSavedValue = isChecked ? "true" : "false";

    // Immediately update UI
    newStates[section][field] = {
      ...newStates[section][field],
      value: isChecked,
      // saved: newSavedValue,
    };

    setCheckboxStates(newStates);
    setUpdatedState(field);
    setUpdatedStateValue(isChecked);

    setLoadingValue((prev) => ({ ...prev, [fullPath]: true }));
    onPublishChange({ path: fullPath, value: newSavedValue })
      .then(() => {
        // Confirm the change was successful and update the saved state
        // setCheckboxStates((prev) => ({
        //   ...prev,
        // }));
      })
      .catch((error) => {
        // Revert the checkbox state in case of an error
        setCheckboxStates((prev) => ({
          ...prev,
          [section]: {
            ...prev[section],
            [field]: {
              ...prev[section][field],
              value: !isChecked,
              saved: !isChecked ? "true" : "false",
            },
          },
        }));
        setDoUpdate(true);
      })
      .finally(() => {
        setLoadingValue((prev) => ({ ...prev, [fullPath]: false }));
        // setDoUpdate(true);
      });
  };

  const handleInputChange = (key, newValue) => {
    setInputValues((prevValues) => ({
      ...prevValues,
      [key]: newValue,
    }));
  };

  const handleInputFocus = (key) => {
    initialInputValuesRef.current[key] = inputValues[key] ?? "";
  };

  const handleInputBlur = (key) => {
    const currentValue = inputValues[key];
    const initialValue = initialInputValuesRef.current[key];
    if (currentValue !== initialValue) {
      setLoadingValue((prev) => ({ ...prev, [key]: true }));

      const updatePath = `analytics.sales.capacity.${key}`;
      onPublishChange({ path: updatePath, value: currentValue })
        .catch((error) => {
          console.error("API call failed for", key, error);
        })
        .finally(() => {
          setLoadingValue((prev) => ({
            ...prev,
            [key]: false,
          }));
        });
    }
  };

  const handleVisibleToChange = (newValue) => {
    if (newValue !== visibleTo) {
      onPublishChange({
        path: "marketing.visibleTo",
        value: newValue,
      })
        .catch((error) => {
          console.error("API call failed for Visible To", error);
        })
        .finally(() => {
          setVisibleTo(newValue);
          if (newValue == "All") {
            onGroupChange([]);
          }
        });
    }
  };
  const handleAddGroups = (newGroups) => {
    const currentGroups = publishingInfo.visibleTo?.saved
      ? publishingInfo.visibleTo?.saved
      : publishingInfo.visibleTo?.value || [];

    const updatedGroups = [
      ...currentGroups,
      ...newGroups.filter(
        (newGroup) =>
          !currentGroups.some(
            (existingGroup) => existingGroup.id === newGroup.id
          )
      ),
    ];

    onGroupChange(updatedGroups);
  };
  const handleRemoveGroup = (groupToDelete) => {
    const currentGroups = publishingInfo.visibleTo?.saved
      ? publishingInfo.visibleTo?.saved
      : publishingInfo.visibleTo?.value || [];

    const updatedGroups = currentGroups.filter(
      (group) => group.id !== groupToDelete.id
    );

    onGroupChange(updatedGroups);
  };

  const renderCheckbox = (section, fieldIdentifier, onChange, isPublish) => {
    const field = isPublish
      ? publishingInfo?.marketing?.visibleCalendar
      : section === "marketing"
      ? checkboxStates[section]?.[fieldIdentifier] ?? {}
      : section === "analytics" && fieldIdentifier === "soldOut"
      ? publishingInfo?.analytics?.sales[fieldIdentifier]
      : publishingInfo?.analytics[fieldIdentifier];
    const isLoading = loadingValue[`${section}.${fieldIdentifier}`];
    const checkedStatus = isPublish
      ? field?.saved ?? field?.value ?? false
      : field?.value ?? false; // Fallback to false if undefined

    const permission = pageMode === "view" ? "read" : field?.permission;
    // console.log(section, fieldIdentifier, permission);
    if (permission === "write" || permission === undefined) {
      return (
        <>
          <span className={styles.checkbox}>
            <Checkbox
              checked={checkedStatus}
              disabled={isLoading}
              onChange={(e) => {
                setDoUpdate(false);
                handleCheckboxChange(
                  section,
                  fieldIdentifier,
                  e.target.checked
                );
              }}
            />
          </span>
        </>
      );
    } else if (permission === "read") {
      return (
        <>
          <span className={styles.textValue}>
            {field === true || checkedStatus == true ? "Yes" : "No"}
          </span>
        </>
      );
    }
    if (permission === "hidden") {
      return null;
    }
  };

  const renderInput = (field, label, key) => {
    const permission = pageMode === "view" ? "read" : field?.permission;

    const isPlatinumDisabled =
      (key === "platinum" || key === "estimatedAttendance") &&
      loadingValue["purchase"];
    const inputValue = inputValues[key] ?? field?.saved ?? field?.value ?? 0;
    const isLoading = loadingValue[key];

    //Adjust alignment of capacity or attendance
    const labelStyle = (() => {
      if (label) {
        return {
          minWidth: "184px",
          display: "inline-block",
        };
      }
    })();

    // If permission is hidden, don't render anything
    if (
      permission === "hidden" ||
      (pageMode === "view" && field?.permission === "hidden")
    ) {
      return null;
    }

    if (permission === "read" || pageMode === "view") {
      return (
        <div className={styles.colValue}>
          <span className={styles.label} style={labelStyle}>
            {label}
          </span>
          <span className={styles.textInput}>{inputValue || 0}</span>
        </div>
      );
    }

    return (
      <>
        {/* {isLoading && !inputValue ? (
          <div className={styles.loader}>
            <div>
              <Loader />
            </div>
          </div>
        ) : ( */}
        <InputItem
          label={`${label}${field?.required == true ? " *" : ""}`}
          value={inputValue}
          // placeholder="0"
          onChange={(e) => handleInputChange(key, e.target.value)}
          onBlur={() => handleInputBlur(key)}
          onFocus={() => handleInputFocus(key)}
          className={isPlatinumDisabled ? styles.disabled : styles.inputItem}
          // disabled={isPlatinumDisabled}
          tabIndex={isPlatinumDisabled ? -1 : 0}
          style={{ cursor: isPlatinumDisabled ? "not-allowed" : "auto" }}
        />
        {/* )} */}
      </>
    );
  };
  useEffect(() => {
    const newVisibleTo =
      publishingInfo?.marketing?.visibleTo?.saved ||
      publishingInfo?.marketing?.visibleTo?.value ||
      "Select";

    if (newVisibleTo !== visibleTo) {
      setVisibleTo(newVisibleTo);
    }
  }, [publishingInfo]);

  const handleHomesiteDatesChange = () => {};

  const renderVisibleTo = () => {
    const visiblePermission =
      pageMode === "view"
        ? "read"
        : publishingInfo?.marketing?.visibleTo?.permission;

    const visibleToValue = publishingInfo?.marketing?.visibleTo?.value;
    const visibleToSaved = publishingInfo?.marketing?.visibleTo?.saved;

    const visibleToValueToRead = publishingInfo?.visibleTo?.saved
      ? publishingInfo?.visibleTo?.saved
      : publishingInfo?.visibleTo?.value || [];

    if (visiblePermission === "write" || visiblePermission === undefined) {
      return (
        <>
          <span className={styles.visibleToSelect}>
            <Select
              placeholder={visibleTo || "Select"}
              value={visibleTo}
              options={["All", "Limit To Groups"]}
              onChange={(value) => handleVisibleToChange(value)}
            />
          </span>
          {visibleTo === "Limit To Groups" && (
            <div className={styles.row}>
              <div className={styles.badgesList}>
                {visibleToValueToRead.map((group, index) => (
                  <div className={styles.speakerItem} key={group.name || index}>
                    {group?.name || "No Name For Group"}
                    <div
                      className={styles.removeSpeaker}
                      onClick={() => handleRemoveGroup(group)}
                    >
                      &times;
                    </div>
                  </div>
                ))}
              </div>
              <div
                className={styles.addBadgesButton}
                onClick={() => {
                  setIsGroupModalOpen(true);
                }}
              >
                <AddButton description={"Add Groups"} color="#2A3847" />
              </div>
            </div>
          )}
        </>
      );
    } else if (visiblePermission === "read") {
      const namesList =
        visibleToValueToRead.length > 0
          ? visibleToValueToRead.map((group) => group.name).join(", ")
          : "N/A";

      return <span className={styles.textValue}>{namesList}</span>;
    } else if (visiblePermission === "hidden") {
      return null;
    }
  };

  const renderCaptionInput = (dataType, label, onChange, onBlur) => {
    let displayValue = "";
    if (dataType === "marketing.primary") {
      displayValue = primaryCaptionText;
    } else if (dataType === "marketing.secondary") {
      displayValue = secondaryCaptionText;
    }

    const permission = (() => {
      if (dataType === "marketing.primary") {
        return publishingInfo?.marketing?.primaryCaption?.permission;
      }
      if (dataType === "marketing.secondary") {
        return publishingInfo?.marketing?.secondaryCaption?.permission;
      }
      return "hidden";
    })();

    if (permission === "hidden") {
      return null;
    }

    if (permission !== "write" || pageMode === "view") {
      return (
        <div className={styles.captionInputWrapper}>
          <span className={styles.viewLabel}>{label}</span>
          <span className={styles.textInput}>{displayValue || "N/A"}</span>
        </div>
      );
    }

    return (
      <div className={styles.captionInputWrapper}>
        <span className={styles.captionLabel}>{label}</span>
        <InputItem
          value={displayValue}
          onChange={(event) => onChange(event)}
          onBlur={onBlur}
          className={styles.captionInput}
          removeLabel={true}
          placeholder="Click to type"
        />
      </div>
    );
  };

  const countingTicketsValue =
    publishingInfo?.analytics?.countingTickets?.saved ||
    publishingInfo?.analytics?.countingTickets?.value;
  const isCountingTicketsChecked =
    countingTicketsValue === true || countingTicketsValue === "Yes";

  const handleAddVisibleTo = () => {
    setVisibleTo("");
    setOpenVisibleToModal(false);
  };

  const handleCloseVisibleModal = () => {
    setVisibleTo("");
    setOpenVisibleToModal(false);
  };

  return (
    <Card
      children={
        <section className={styles.container}>
          <div className={styles.title}>
            {isLocal2 ? "Dates" : "Publishing Information"}
          </div>
          <hr className={styles.hr} />

          <div className={styles.datesSection}>
            {publishingInfo && isLocal2 && (
              <DateV2Section
                ruleset={publishingInfo?.event?.ruleset?.value}
                eventName={publishingInfo?.event?.name?.value}
                dateInfo={publishingInfo?.event}
                onDateTimeChange={(keyOrUpdates, newValue, isTime) => {
                  if (
                    typeof keyOrUpdates === "object" &&
                    keyOrUpdates !== null
                  ) {
                    onDateTimeChange(keyOrUpdates, newValue, isTime);
                    return;
                  }

                  const key = keyOrUpdates;

                  if (key === "eventStartDate" && publishOpen) {
                    const eventStartTimeZone =
                      publishingInfo?.event?.dates?.eventStartTimeZone?.saved ??
                      publishingInfo?.event?.dates?.eventStartTimeZone?.value ??
                      PACIFIC_TIMEZONE_LABEL;

                    const twoWeeksBefore = buildPacificRulePurchaseStartDate(
                      newValue,
                      eventStartTimeZone,
                      14
                    );

                    const eventEndDate =
                      publishingInfo?.event?.dates?.eventEndDate?.saved ??
                      publishingInfo?.event?.dates?.eventEndDate?.value ??
                      null;

                    onDateTimeChange(
                      {
                        purchaseStartDate: twoWeeksBefore,
                        purchaseEndDate: eventEndDate,
                      },
                      null,
                      false
                    );

                    onSetHomesiteTicketChange(newValue);
                  }

                  onDateTimeChange(key, newValue, isTime);
                }}
                pageMode={pageMode}
                section="Event Information"
                timezonePermission={pageMode === "view" ? "read" : "write"}
                isLocal2={true}
              />
            )}
          </div>

          {isLocal2 && <div className={styles.hr} />}

          {isLocal2 && (
            <>
              <div className={styles.row}>
                <div className={styles.tentative2}>
                  <span className={styles.label2}>
                    Tickets on Sale: Follow HomeSite ticket sales rules
                  </span>
                  <span className={styles.checkbox}>
                    <Checkbox
                      checked={publishOpen}
                      onChange={(e) => {
                        const newChecked = e.target.checked;

                        if (newChecked) {
                          handleHomesiteDatesChange(newChecked);
                          const eventDateRaw =
                            publishingInfo?.event?.dates?.eventStartDate
                              ?.saved ??
                            publishingInfo?.event?.dates?.eventStartDate
                              ?.value ??
                            "";

                          if (eventDateRaw) {
                            setPublishOpen(true);
                            setDoUpdate2(true);
                            onSetHomesiteTicketChange(eventDateRaw);
                          } else {
                            createNotification({
                              type: "danger",
                              title: "Error",
                              message: "You must set an Event Date first",
                              autoDismiss: true,
                            });
                          }
                        } else {
                          setPublishOpen(false);
                          setDoUpdate2(false);
                        }
                      }}
                    />
                  </span>
                </div>
              </div>

              {!publishOpen && (
                <div className={styles.datesSection}>
                  {publishingInfo && (
                    <DateV2Section
                      location={publishingInfo?.event?.location?.value}
                      ruleset={publishingInfo?.event?.ruleset?.value}
                      eventName={publishingInfo?.event?.name?.value}
                      dateInfo={publishingInfo?.event}
                      onDateTimeChange={onDateTimeChange}
                      pageMode={pageMode}
                      section="Publishing Information"
                      timezonePermission={
                        pageMode === "view" ? "read" : "write"
                      }
                      isLocal={isRulesetLocal === "Local v2.0"}
                      onlyShowDateKey="purchaseStartDate" //visibleOnCalendarDate
                    />
                  )}
                </div>
              )}
            </>
          )}
          {isLocal2 && <div className={styles.hr} />}
          {isLocal2 && (
            <>
              <div className={styles.row}>
                <div className={styles.tentative}>
                  <span className={styles.label}>Publish to Calendar</span>
                  {renderCheckbox(
                    "marketing",
                    "visibleCalendar",
                    onPublishChange,
                    true
                  )}
                </div>
              </div>

              {ticketsSaleOpen && (
                <div className={styles.datesSection}>
                  {publishingInfo && (
                    <DateV2Section
                      location={publishingInfo?.event?.location?.value}
                      ruleset={publishingInfo?.event?.ruleset?.value}
                      eventName={publishingInfo?.event?.name?.value}
                      dateInfo={publishingInfo?.event}
                      onDateTimeChange={onDateTimeChange}
                      pageMode={pageMode}
                      section="Publishing Information"
                      timezonePermission={
                        pageMode === "view" ? "read" : "write"
                      }
                      isLocal={isRulesetLocal === "Local v2.0"}
                      onlyShowDateKey="visibleOnCalendarDate"
                    />
                  )}
                </div>
              )}
            </>
          )}
          {isLocal2 && <div className={styles.hr} />}
          <div className={styles.row}>
            <div className={styles.tentative}>
              <span className={styles.label}>Tentative</span>
              {renderCheckbox("marketing", "tentative", onPublishChange)}
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.tentative}>
              <span className={styles.label}>Active (Visible to IBOs)</span>
              {renderCheckbox("marketing", "active", onPublishChange)}
            </div>
          </div>
          {isRulesetLocal === "Local v2.0" ? (
            <>
              {isCountingTicketsChecked ? (
                <>
                  <div className={styles.colInput}>
                    {renderInput(
                      publishingInfo?.analytics?.sales?.capacity?.purchase,
                      "Purchase Capacity",
                      "purchase"
                    )}
                  </div>

                  {/* {loadingValue["purchase"] !== true && ( */}
                  <div className={styles.colInput}>
                    {renderInput(
                      publishingInfo?.analytics?.sales?.capacity
                        ?.estimatedAttendance,
                      "Estimated Attendance",
                      "estimatedAttendance"
                    )}
                  </div>
                  {/* )} */}
                </>
              ) : null}
            </>
          ) : (
            <>
              {isCountingTicketsChecked ? (
                <>
                  <div className={styles.colInput}>
                    {renderInput(
                      publishingInfo?.analytics?.sales?.capacity?.purchase,
                      "Purchase Capacity",
                      "purchase"
                    )}
                  </div>

                  {/* {loadingValue["purchase"] !== true && ( */}
                  <div className={styles.colInput}>
                    {renderInput(
                      publishingInfo?.analytics?.sales?.capacity?.platinum,
                      "Platinum Capacity",
                      "platinum"
                    )}
                  </div>
                  {/* )} */}
                </>
              ) : null}
            </>
          )}
          <div className={styles.row}>
            <div className={styles.visibleToCol}>
              <span className={styles.label}>
                Visible to{" "}
                {isRequired.visibleTo && pageMode == "edit" ? " *" : ""}
              </span>

              {renderVisibleTo()}
            </div>
          </div>
          {!isLocal2 && (
            <div className={styles.datesSection}>
              {publishingInfo && (
                <DateV2Section
                  location={publishingInfo?.event?.location?.value}
                  ruleset={publishingInfo?.event?.ruleset?.value}
                  eventName={publishingInfo?.event?.name?.value}
                  dateInfo={publishingInfo?.event}
                  onDateTimeChange={onDateTimeChange}
                  pageMode={pageMode}
                  section="Publishing Information"
                  timezonePermission={pageMode === "view" ? "read" : "write"}
                  isLocal={isRulesetLocal === "Local v2.0"}
                />
              )}
            </div>
          )}
          <VisibleToModal
            isOpen={openVisibleToModal}
            onCollapse={handleCloseVisibleModal}
            onClick={handleAddVisibleTo}
          />
          <AddGroupModal
            isOpen={isGroupModalOpen}
            onCollapse={() => setIsGroupModalOpen(false)}
            onClick={(newGroups) => handleAddGroups(newGroups)}
            groups={
              homesitesGroups ? [...groups, ...homesitesGroups] : [...groups]
            }
            groupsLoading={isGroupsLoading}
          />
        </section>
      }
    />
  );
}

PublishingV2InfoForm.propTypes = {
  visibleOnDate: PropTypes.string,
  setVisibleOnDate: PropTypes.func,
  visibleOnTime: PropTypes.string,
  setVisibleOnTime: PropTypes.func,
  startFeeDate: PropTypes.string,
  setStartFeeDate: PropTypes.func,
  startFeeTime: PropTypes.string,
  setStartFeeTime: PropTypes.func,
  changeableUntilDate: PropTypes.string,
  setChangeableUntilDate: PropTypes.func,
  changeableUntilTime: PropTypes.string,
  setChangeableUntilTime: PropTypes.func,
  purchaseBeginDate: PropTypes.string,
  setPurchaseBeginDate: PropTypes.func,
  purchaseBeginTime: PropTypes.string,
  setPurchaseBeginTime: PropTypes.func,
  purchaseEndTime: PropTypes.string,
  setPurchaseEndTime: PropTypes.func,
  purchaseEndDate: PropTypes.string,
  setPurchaseEndDate: PropTypes.func,
  visibleTo: PropTypes.string,
  setVisibleTo: PropTypes.func,
};
